import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import React from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Grid, IconButton, Theme, Typography } from '@material-ui/core';
import { Cancel, CheckCircle, Close, Launch, RemoveCircle, Warning } from '@material-ui/icons';
import Icon from '@mdi/react';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { convertToRaw, EditorState } from 'draft-js';
import { makeStyles, createStyles } from '@material-ui/styles';
import { DataGrid, GridColumns, GridCellParams, GridSortModel } from '@material-ui/data-grid';
import { isEqual } from 'lodash';
import { unpackToDate, unpackToDateTime } from '@campfire/hot-date';
import { ConfirmationDialog } from '../../../../common/dialogs/ConfirmationDialog';
import { iconList } from '../../../../common/icons/RoleIconList';
import { getStatusColors } from '../../../../common/cards/ExpandableCard';
import { VolunteerTaskViewDialog } from '../../volunteer-common-profile/profile-sections/tasks-section/VolunteerCompletedTaskItem';
import { VolunteerTaskAddDialog } from '../../volunteer-common-profile/profile-sections/tasks-section/VolunteerTaskAddDialog';
import { VolunteerSubmitValues } from '../../../admin/tasks/VolunteerSubmit/VolunteerSubmitValues';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useUser } from '../../../../global/auth/useUser';
import { SearchField } from '../../../../common/inputs/SearchField';
import { VolunteerProfile } from '../../../../common/AvatarName';
import { TemplateSnackbar, SnackBarOptions } from '../../../../common/snackbars/TemplateSnackbar';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { GET_VOLUNTEERS_ROLE_APPLICATIONS } from './VolunteersRoleApplicationsScreen.gql';
import { RoleApplicationsMenuButton, RoleApplicationAction } from './RoleApplicationsMenu';
import { useApproveRoleApplicationFetch, useRejectRoleApplicationFetch } from './role-application-actions';

const STATUS_LABELS = new Map<string, string>([
  ['pending', 'Pending'],
  ['in_progress', 'In progress'],
  ['awaiting_review', 'Awaiting review'],
]);

const STATUS_COLORS = new Map<string, string>([
  ['pending', '#F4DA75'],
  ['in_progress', '#F4DA75'],
  ['awaiting_review', '#F4DA75'],
]);

const useTableStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      [theme.breakpoints.down('xs')]: {
        height: '560px',
      },
      '& .MuiDataGrid-columnSeparator': {
        display: 'none',
      },
      '& .MuiDataGrid-selectedRowCount': {
        visibility: 'hidden',
      },
      '& .MuiDataGrid-columnHeader--moving': {
        background: 'none',
      },
      '& .MuiDataGrid-columnHeaderTitleContainer': {
        padding: 0,
      },
    },
  })
);

interface ApplicableTask {
  taskId: string;
  title: string;
}

interface CompletedTaskRef {
  completedTaskId: string;
  status: string;
  dateSubmitted: string;
  task: { taskId: string };
}

interface RoleApplicationRow {
  id: string;
  status: string;
  statusLabel: string;
  roleName: string;
  roleId: string;
  roleIcon: string | null;
  volunteerId: string;
  volunteerName: string;
  profile: any;
  email: string;
  phone: string;
  dateApplied: any;
  tasksTotal: number;
  tasksCompleted: number;
  applicableTasks: ApplicableTask[];
  completedTasks: CompletedTaskRef[];
}

export function VolunteersRoleApplicationsScreen() {
  const { data, loading, refetch } = useCampfireQuery<any, {}>(GET_VOLUNTEERS_ROLE_APPLICATIONS);
  const { maybeManagerIdentity } = useUser();
  const managerId = maybeManagerIdentity?.managerId;

  const [filterValue, setFilterValue] = React.useState('');
  const [snackbar, setSnackbar] = React.useState<SnackBarOptions>();
  const [sortModel, setSortModel] = React.useState<GridSortModel>([{ field: 'dateApplied', sort: 'desc' }]);
  const [rejectingId, setRejectingId] = React.useState<string | null>(null);
  const [rejectReason, setRejectReason] = React.useState<EditorState>(EditorState.createEmpty());
  const [tasksRowId, setTasksRowId] = React.useState<string | null>(null);
  const [openedTaskId, setOpenedTaskId] = React.useState<string | null>(null);

  const approveFetch = useApproveRoleApplicationFetch();
  const rejectFetch = useRejectRoleApplicationFetch();

  const refresh = () => {
    if (refetch) {
      setTimeout(refetch, 0);
    }
  };

  const rows: RoleApplicationRow[] = (data?.vm.activeRoleApplications ?? [])
    .filter((ra: any) => !ra.volunteer?.dateDeactivated)
    .map((ra: any) => ({
      id: ra.roleApplicationId,
      status: ra.status,
      statusLabel: STATUS_LABELS.get(ra.status) ?? ra.status,
      roleName: ra.role.name,
      roleId: ra.role.activityRoleId,
      roleIcon: ra.role.icon ?? null,
      volunteerId: ra.volunteer.volunteerId,
      volunteerName: `${ra.volunteer.profile.preferredName} ${ra.volunteer.profile.lastName}`,
      profile: ra.volunteer.profile,
      email: ra.volunteer.profile.email ?? '',
      phone: ra.volunteer.profile.contactNumber ?? '',
      dateApplied: unpackToDateTime(ra.dateApplied),
      tasksTotal: ra.tasksTotal ?? 0,
      tasksCompleted: ra.tasksCompleted ?? 0,
      applicableTasks: ra.applicableTasks ?? [],
      completedTasks: ra.volunteer.profile.completedTasks ?? [],
    }));

  const showingRows = rows.filter((row) =>
    `${row.volunteerName} ${row.email} ${row.phone} ${row.roleName}`.toLowerCase().includes(filterValue.toLowerCase())
  );

  const onApprove = (id: string) => {
    if (!managerId) {
      setSnackbar({ open: true, variant: 'error', message: 'Manager identity not available' });
      return;
    }
    approveFetch.run({ roleApplicationId: id, managerId }).then(() => {
      setSnackbar({ open: true, variant: 'success', message: 'Application approved' });
      refresh();
    });
  };

  const openReject = (id: string) => {
    setRejectingId(id);
    setRejectReason(EditorState.createEmpty());
  };

  const closeReject = () => {
    setRejectingId(null);
    setRejectReason(EditorState.createEmpty());
  };

  const confirmReject = () => {
    if (!rejectingId) return;
    if (!managerId) {
      setSnackbar({ open: true, variant: 'error', message: 'Manager identity not available' });
      return;
    }
    const content = rejectReason.getCurrentContent();
    const payload: { roleApplicationId: string; managerId: string; reason?: string } = {
      roleApplicationId: rejectingId,
      managerId,
    };
    if (content.hasText()) {
      payload.reason = JSON.stringify(convertToRaw(content));
    }
    rejectFetch.run(payload).then(() => {
      setSnackbar({ open: true, variant: 'success', message: 'Application rejected' });
      closeReject();
      refresh();
    });
  };

  const onAction = (action: RoleApplicationAction, id: string) => {
    if (action === 'approve') {
      onApprove(id);
    } else if (action === 'reject') {
      openReject(id);
    }
  };

  const approveLabelFor = (status: string) => {
    if (status === 'pending') return 'Approve to start';
    if (status === 'awaiting_review') return 'Approve';
    return 'Approve';
  };

  const columns: GridColumns = [
    {
      field: 'volunteerName',
      headerName: 'Volunteer',
      flex: 1,
      minWidth: 220,
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <VolunteerProfile
            inTable
            noTrim
            {...row.profile}
            name={`${row.profile.preferredName} ${row.profile.lastName}`}
          />
        );
      },
    },
    {
      field: 'roleName',
      headerName: 'Role',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 180,
      renderCell: ({ row }: any) => {
        const iconPath = row.roleIcon ? (iconList as Record<string, string>)[row.roleIcon] : undefined;
        return (
          <Box display='flex' alignItems='center'>
            {iconPath && (
              <Box marginRight='8px' display='flex' alignItems='center' flexShrink={0}>
                <Icon path={iconPath} size={0.8} color={theme.color.grey.neutral400} />
              </Box>
            )}
            <Typography variant='body2'>{row.roleName}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'statusLabel',
      headerName: 'Status',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 160,
      renderCell: ({ row }: any) => (
        <Box display='flex' alignItems='center'>
          <Box
            height='10px'
            width='10px'
            borderRadius='50%'
            marginRight='8px'
            bgcolor={STATUS_COLORS.get(row.status) ?? '#DCDCDC'}
          />
          <Typography variant='caption' style={{ fontSize: 13 }}>
            {row.statusLabel}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'tasksProgress',
      headerName: 'Tasks',
      flex: 0.6,
      disableColumnMenu: true,
      minWidth: 120,
      sortable: false,
      renderCell: ({ row }: any) => {
        if (row.tasksTotal === 0) {
          return (
            <Typography variant='caption' style={{ fontSize: 13, color: '#9E9E9E' }}>
              No tasks
            </Typography>
          );
        }
        const allDone = row.tasksCompleted >= row.tasksTotal;
        const colour = allDone ? '#3F8C3F' : '#C97A1A';
        return (
          <Box display='flex' alignItems='center' onClick={() => setTasksRowId(row.id)} style={{ cursor: 'pointer' }}>
            <Typography
              variant='caption'
              style={{
                fontSize: 13,
                color: colour,
                fontWeight: 600,
                textDecoration: 'underline',
              }}
            >
              {row.tasksCompleted} / {row.tasksTotal}
            </Typography>
            <Launch style={{ fontSize: 14, color: colour, marginLeft: 6 }} />
          </Box>
        );
      },
    },
    {
      field: 'dateApplied',
      headerName: 'Date Applied',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 160,
      renderCell: ({ row }: any) => row.dateApplied.toFormat('dd/MM/y'),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 200,
      renderCell: ({ row }: any) => row.profile.email,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 160,
      renderCell: ({ row }: any) => row.profile.contactNumber,
    },
    {
      field: '',
      disableColumnMenu: true,
      headerName: '',
      sortable: false,
      width: 64,
      renderCell: ({ row }: any) => (
        <RoleApplicationsMenuButton
          onAction={(action: RoleApplicationAction) => onAction(action, row.id)}
          bulk={false}
          approveLabel={approveLabelFor(row.status)}
        />
      ),
    },
  ];

  const onSortChange = (model: GridSortModel) => {
    if (!isEqual(model, sortModel)) {
      setSortModel(model);
    }
  };

  const tableClasses = useTableStyles();
  const { isMobile, theme } = useCampfireTheme();

  if (loading) {
    return (
      <Box position='relative' display='flex' flex='1 1 auto' height={1}>
        <CircularProgressOverlay isLoading />
      </Box>
    );
  }

  return (
    <Box
      flex={1}
      display='flex'
      flexDirection='column'
      paddingLeft={isMobile ? '16px' : '32px'}
      paddingRight='16px'
      paddingBottom='5px'
    >
      <Box
        display={isMobile ? 'block' : 'flex'}
        alignItems='center'
        paddingTop='16px'
        paddingBottom='16px'
        justifyContent='space-between'
      >
        <Typography style={{ fontSize: '15px', marginBottom: isMobile ? '8px' : '' }}>
          You have <strong>{rows.length}</strong> active role applications
        </Typography>
        <Box display='flex' alignItems='center'>
          <Box display='flex' alignItems='stretch'>
            <SearchField
              fullWidth
              value={filterValue}
              onChange={(e: any) => setFilterValue(e.target.value)}
              placeholder={isMobile ? 'Search' : 'Search role applications'}
            />
          </Box>
        </Box>
      </Box>
      <Box flex={1} display='flex' height='100%'>
        <DataGrid
          columns={columns}
          rows={showingRows}
          classes={tableClasses}
          disableSelectionOnClick
          sortModel={sortModel}
          onSortModelChange={onSortChange}
        />
      </Box>
      {snackbar && <TemplateSnackbar {...snackbar} onClose={() => setSnackbar(undefined)} />}
      {(() => {
        const tasksRow = tasksRowId ? rows.find((r) => r.id === tasksRowId) : null;
        if (!tasksRow) return null;
        const completedById = new Map(tasksRow.completedTasks.map((ct) => [ct.task.taskId, ct]));
        const itemStatus = (taskId: string): 'completed' | 'na' | 'pending' | 'new' => {
          const status = completedById.get(taskId)?.status;
          if (status === 'approved' || status === 'admin-approved') return 'completed';
          if (status === 'pending') return 'pending';
          if (status === 'not-applicable') return 'na';
          return 'new';
        };
        const openedTask = openedTaskId ? tasksRow.applicableTasks.find((t) => t.taskId === openedTaskId) : null;
        const openedCompleted = openedTaskId ? completedById.get(openedTaskId) : undefined;
        const closeTask = () => setOpenedTaskId(null);
        return (
          <>
            <Dialog open={tasksRowId !== null} onClose={() => setTasksRowId(null)} maxWidth='sm' fullWidth>
              <DialogTitle
                style={{
                  borderBottom: `1px solid ${theme.color.grey.border}`,
                  padding: '8px 24px',
                }}
                disableTypography
              >
                <Grid container alignItems='flex-start' justify='space-between'>
                  <Grid item xs>
                    <Typography variant='h6' style={{ fontWeight: 'bold' }}>
                      {tasksRow.roleName}
                    </Typography>
                    <Typography variant='subtitle1' color='textSecondary' style={{ lineHeight: 1.3 }}>
                      {`${tasksRow.volunteerName} — tasks required for this role.`}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <IconButton aria-label='Close' color='inherit' onClick={() => setTasksRowId(null)}>
                      <Close color='action' />
                    </IconButton>
                  </Grid>
                </Grid>
              </DialogTitle>
              <DialogContent style={{ padding: 0, margin: '24px 0' }}>
                <Grid container direction='column' style={{ width: '100%', padding: '0 24px' }}>
                  {tasksRow.applicableTasks.length === 0 ? (
                    <Typography variant='body2' style={{ color: '#9E9E9E' }}>
                      No tasks for this role.
                    </Typography>
                  ) : (
                    tasksRow.applicableTasks.map((task) => {
                      const status = itemStatus(task.taskId);
                      const colours = getStatusColors(status, theme);
                      const completed = completedById.get(task.taskId);
                      const subLine = completed
                        ? `${
                            completed.status === 'approved' || completed.status === 'admin-approved'
                              ? 'Completed'
                              : completed.status === 'pending'
                              ? 'Pending approval, submitted'
                              : completed.status === 'not-applicable'
                              ? 'Marked not applicable'
                              : 'Submitted'
                          } on ${unpackToDate(completed.dateSubmitted).toFormat('d LLLL y')}`
                        : 'Not started';
                      return (
                        <Box
                          key={task.taskId}
                          onClick={() => setOpenedTaskId(task.taskId)}
                          display='flex'
                          padding='16px'
                          marginBottom='8px'
                          alignItems='center'
                          bgcolor={colours.cardBackgroundColor}
                          style={{ cursor: 'pointer', borderRadius: 4 }}
                        >
                          <Box minWidth='22px' display='flex' alignItems='center'>
                            {status === 'completed' ? (
                              <CheckCircle style={{ fontSize: 22, color: colours.iconColor }} />
                            ) : status === 'pending' ? (
                              <Warning style={{ fontSize: 22, color: colours.iconColor }} />
                            ) : status === 'na' ? (
                              <RemoveCircle style={{ fontSize: 22, color: colours.iconColor }} />
                            ) : (
                              <Cancel style={{ fontSize: 22, color: colours.iconColor }} />
                            )}
                          </Box>
                          <Box paddingLeft='10px'>
                            <Typography style={{ color: colours.textColor, fontWeight: 600 }}>{task.title}</Typography>
                            <Typography style={{ color: colours.textColor, fontSize: '12px', lineHeight: '14px' }}>
                              {subLine}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Grid>
              </DialogContent>
            </Dialog>
            {openedTask && openedCompleted && openedCompleted.status === 'pending' ? (
              <VolunteerSubmitValues
                completedTaskId={openedCompleted.completedTaskId}
                onClose={closeTask}
                onUpdate={refresh}
              />
            ) : null}
            {openedTask && openedCompleted && openedCompleted.status !== 'pending' ? (
              <VolunteerTaskViewDialog
                open
                onClose={closeTask}
                completedTaskId={openedCompleted.completedTaskId}
                taskId={openedTask.taskId}
                status={openedCompleted.status}
                userId={tasksRow.profile.userId}
                profileId={tasksRow.profile.profileId}
              />
            ) : null}
            {openedTask && !openedCompleted ? (
              <VolunteerTaskAddDialog
                open
                onClose={closeTask}
                taskId={openedTask.taskId}
                userId={tasksRow.profile.userId}
                profileId={tasksRow.profile.profileId}
              />
            ) : null}
          </>
        );
      })()}
      <ConfirmationDialog
        open={rejectingId !== null}
        title='Reject application'
        body={
          <Box>
            <Typography gutterBottom>
              Optionally include a reason. If provided, it will be shared with the volunteer in the rejection email.
            </Typography>
            <Box mt='0.5rem'>
              <CampfireRichEditor editorState={rejectReason} setEditorState={setRejectReason} />
            </Box>
          </Box>
        }
        closeActionText='Cancel'
        confirmActionText='Reject'
        confirmActionColor='error'
        handleCloseClick={() => closeReject()}
        handleConfirmClick={confirmReject}
      />
    </Box>
  );
}
